import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { DEFAULT_THEME_KEY, THEME_SWITCH_KEY } from '@/lib/customisation/apply';

/** What the user picked. `system` follows the operating system. */
export type ThemePreference = 'dark' | 'light' | 'system';
/** What is actually applied. */
export type Theme = 'dark' | 'light';

const STORAGE_KEY = 'hearth.theme';
const QUERY = '(prefers-color-scheme: dark)';

interface ThemeContextValue {
  /** The applied theme, for components that need to know (Monaco, toasts). */
  theme: Theme;
  /** The visitor's choice, or the tenant's default when they haven't made one. */
  preference: ThemePreference;
  setPreference: (preference: ThemePreference) => void;
  /** False when the tenant's branding pins the theme; the menu is hidden then. */
  canSwitch: boolean;
  /**
   * The tenant's default (what a visitor gets before they choose) and whether they may
   * choose at all. Called by SiteProvider once the site is known.
   */
  setDefault: (preference: ThemePreference, allowSwitch: boolean) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

const isPreference = (v: unknown): v is ThemePreference =>
  v === 'dark' || v === 'light' || v === 'system';

function readUserPreference(): ThemePreference | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return isPreference(stored) ? stored : null;
  } catch {
    // Storage can be unavailable (private mode): fall back to the default.
    return null;
  }
}

/** The branded default theme.js cached on the last visit, so React starts where it did. */
function readCachedDefault(): { preference: ThemePreference; allowSwitch: boolean } {
  try {
    const stored = localStorage.getItem(DEFAULT_THEME_KEY);
    return {
      preference: isPreference(stored) ? stored : 'dark',
      allowSwitch: localStorage.getItem(THEME_SWITCH_KEY) !== 'off',
    };
  } catch {
    return { preference: 'dark', allowSwitch: true };
  }
}

const resolve = (preference: ThemePreference): Theme =>
  preference === 'system' ? (matchMedia(QUERY).matches ? 'dark' : 'light') : preference;

function apply(theme: Theme, animate: boolean) {
  const root = document.documentElement;
  if (animate) {
    // Fade colours only while switching, so ordinary hover states aren't slowed down.
    root.classList.add('theme-switching');
    window.setTimeout(() => root.classList.remove('theme-switching'), 250);
  }
  root.classList.toggle('dark', theme === 'dark');
}

/** theme.js applies the saved theme before paint; this keeps React in sync with it. */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [userPreference, setUserPreference] = useState<ThemePreference | null>(readUserPreference);
  const [fallback, setFallback] = useState(readCachedDefault);
  const [theme, setTheme] = useState<Theme>(() =>
    document.documentElement.classList.contains('dark') ? 'dark' : 'light',
  );
  const canSwitch = fallback.allowSwitch;
  const preference = canSwitch && userPreference ? userPreference : fallback.preference;

  // Apply whatever the effective preference resolves to, animated after the first paint.
  const mounted = useRef(false);
  useEffect(() => {
    const resolved = resolve(preference);
    if (resolved !== (document.documentElement.classList.contains('dark') ? 'dark' : 'light'))
      apply(resolved, mounted.current);
    mounted.current = true;
    setTheme(resolved);
  }, [preference]);

  const setPreference = useCallback((next: ThemePreference) => {
    setUserPreference(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // The choice still applies for this session.
    }
  }, []);

  const setDefault = useCallback((next: ThemePreference, allowSwitch: boolean) => {
    setFallback((prev) =>
      prev.preference === next && prev.allowSwitch === allowSwitch
        ? prev
        : { preference: next, allowSwitch },
    );
  }, []);

  // While on "system", follow the OS when it changes.
  useEffect(() => {
    if (preference !== 'system') return;
    const media = matchMedia(QUERY);
    const onChange = () => {
      const resolved = resolve('system');
      apply(resolved, true);
      setTheme(resolved);
    };
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [preference]);

  // Keep several open tabs in step.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setUserPreference(readUserPreference());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const value = useMemo(
    () => ({ theme, preference, setPreference, canSwitch, setDefault }),
    [theme, preference, setPreference, canSwitch, setDefault],
  );
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside <ThemeProvider>');
  return ctx;
}
